/**
 * The public landing page — `/`.
 *
 * Janeks' brief, in order: a header with logo, menu, mute and a Play button
 * (burger on mobile); a hero over a silent view of the settlement campfire; a
 * block on levelling and progression; one on how much content there is; one on
 * crafting and the hideout; one on PvP/PvE; a footer with the policies from
 * the admin panel.
 *
 * ─── It is not part of the game view ────────────────────────────────────────
 * Everything under `base-view/` assumes a signed-in player with a character
 * sheet. This page has neither, and the one piece it borrows — the settlement
 * scene — it borrows through `landing-scene.ts`, which touches no store and
 * opens no socket. Nothing here calls anything that needs a token.
 *
 * ─── The 3D scene is a PAGE-LEVEL backdrop, not part of this page ───────────
 * It used to render into the hero. It now lives behind `#app` and is owned by
 * `landing-backdrop.ts`, because the camera has to keep moving across the
 * navigation to `/login` — a scene inside this page's markup is destroyed by
 * the router the moment that navigation happens.
 *
 * It is still lazy and still optional: a dynamic `import()`, never fired on a
 * narrow screen or under `prefers-reduced-motion`, and those visitors get the
 * poster — a 100 KB still of the same shot — exactly as before.
 *
 * Measured, because the obvious claim is wrong: the deferred CHUNK is only
 * ~36 KB. Three.js is NOT deferred — `main.ts` statically imports
 * `engine/bootstrap`, which reaches base-view, which imports three, so it is
 * in the main bundle on every route including `/login` and always has been.
 * What the gate actually saves is the ASSETS: **8.6 MB across 70 distinct
 * GLBs and textures**, fetched at runtime only once the scene starts. That is
 * the number worth protecting, and splitting three out of the entry bundle to
 * chase the other 36 KB would be a change to the app's entry point for a
 * rounding error next to it.
 */
import { BRAND, SECTIONS } from './landing-copy';
import { injectLandingStyles } from './landing-styles';
import { footerMarkup, headerMarkup, playCta, wireChrome } from './landing-chrome';
import { ICON_CHEVRON_DOWN, ICON_PLAY } from './landing-icons';
import { escapeAttr, escapeHtml } from './landing-escape';
import { backdropSupported } from './landing-backdrop';
import { loadNews } from './landing-cms';
import { newsCardMarkup } from './news-card';
import { cancelAuthTransition, transitionToAuth } from './auth-transition';
import type { LandingPreview, PreviewKind } from './landing-preview';

/**
 * How many dispatches the landing strip shows. Janeks: *"Latest 3 news,
 * inline."* Three is also what the grid is built around — `.lp-news-home` is
 * three columns on a wide screen and one on a narrow one, so a fourth card
 * would either orphan itself on a second row or force a layout that disagrees
 * with the brief.
 */
const LANDING_NEWS_COUNT = 3;

export interface LandingOptions {
  /** Navigate within the SPA. Supplied by `main.ts` so this module never touches history directly. */
  navigate: (to: string) => void;
}

export function renderLanding(root: HTMLElement, opts: LandingOptions): () => void {
  const removeStyles = injectLandingStyles();
  const cleanups: Array<() => void> = [removeStyles];

  root.innerHTML = markup();
  const page = root.querySelector('.lp') as HTMLElement;
  // The other half of the hand-over: the page leaves toward the left and comes
  // back from it. A CSS animation, so it plays from the moment the class is
  // here with no second frame to arrange — see `.lp-entering`.
  page.classList.add('lp-entering');

  cleanups.push(wireChrome(page, {
    onLanding: true,
    // Play does not navigate straight away: it pans the backdrop and slides
    // the page out first, then lets the router swap in the form. See
    // `auth-transition.ts`.
    navigate: (to) => {
      if (to === '/play') { opts.navigate(to); return; }
      transitionToAuth(page, to, opts.navigate);
    },
  }));
  // A teardown for any other reason — the back button, a footer link — must
  // not leave a queued navigation to fire afterwards.
  cleanups.push(cancelAuthTransition);

  // ── reveal on scroll ──────────────────────────────────────────────────────
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealables = [...page.querySelectorAll<HTMLElement>('.lp-reveal')];
  if (reducedMotion) {
    for (const el of revealables) el.dataset.shown = 'true';
  } else {
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        (entry.target as HTMLElement).dataset.shown = 'true';
        io.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -12% 0px' });
    for (const el of revealables) io.observe(el);
    cleanups.push(() => io.disconnect());
  }

  // ── the backdrop ──────────────────────────────────────────────────────────
  //
  // The settlement is NOT rendered into this page and is not owned by it. It
  // is a layer behind `#app` that `main.ts` raises and lowers per route (see
  // `landing-backdrop.ts`), because it has to outlive this page's teardown —
  // the router destroys this page before mounting the login form, so anything
  // this page owned would be gone before the camera could move.
  //
  // The hero keeps its poster as the fallback for everyone the live scene is
  // not run for; `.lp-backdrop-live` on `<body>` hides it when it is.

  // ── the two turntables beside the copy ────────────────────────────────────
  //
  // Same gate as the hero and for the same reasons: they are decoration on a
  // page whose job is to load fast, they each hold a WebGL context, and on a
  // narrow screen the slot is a 300 px box that the copy would rather have.
  // Loaded through a dynamic import so none of `landing-preview`'s code — or
  // the character and weapon GLBs it fetches — is touched otherwise.
  const previews: LandingPreview[] = [];
  let previewsCancelled = false;
  // `backdropSupported()`, NOT `backdropIsLive()`. The latter asks whether the
  // scene has finished loading, and the scene is fetched asynchronously by an
  // acquire that has only just been issued — so it is reliably FALSE here, and
  // gating on it meant the turntables never started at all. This is the
  // synchronous capability question: wide enough, and motion allowed.
  if (backdropSupported()) {
    for (const host of page.querySelectorAll<HTMLElement>('[data-preview]')) {
      const kind = host.dataset.preview as PreviewKind;
      void import('./landing-preview')
        .then(({ startPreview }) => (previewsCancelled ? null : startPreview(host, kind)))
        .then((preview) => {
          if (!preview) return;
          if (previewsCancelled) { preview.dispose(); return; }
          previews.push(preview);
        })
        .catch((err) => {
          // A missing GLB, no WebGL, a chunk that failed. `.lp-preview:empty`
          // collapses the slot, so the section reflows to full width and
          // nothing looks broken.
          console.warn(`[landing] ${kind} preview unavailable`, err);
        });
    }
  }
  cleanups.push(() => {
    previewsCancelled = true;
    for (const p of previews) p.dispose();
    previews.length = 0;
  });

  // ── "What's new" — the three latest dispatches, fetched ──────────────────
  //
  // The only asynchronous DOM mutation on this page, which makes it the only
  // one that can land after a teardown: the Play transition destroys this
  // page on a timer while this request is still in the air. Hence `cancelled`
  // in `cleanups`, the same guard `news-view.ts` carries.
  //
  // The section is REMOVED when there is nothing to show rather than left
  // saying "nothing yet" — a board with no notices is honest on `/news`,
  // where the reader asked for the board, but on a landing page it is a dead
  // block the visitor did not ask for. Three things produce it and none is a
  // fault: a preview build (which empties this endpoint by design), a
  // deployment that has published nothing, and an API that is not up yet —
  // `loadNews` resolves empty for all three rather than rejecting.
  const newsSection = page.querySelector<HTMLElement>('[data-news-section]');
  let newsCancelled = false;
  cleanups.push(() => { newsCancelled = true; });
  if (newsSection) {
    const list = newsSection.querySelector<HTMLElement>('[data-news-list]')!;
    void loadNews().then((result) => {
      if (newsCancelled) return;
      // `.slice(0, 3)` client-side: `GET /cms/news` hardcodes a page size of
      // 20 in core and takes no limit, and core may not be edited here.
      const latest = result.items.slice(0, LANDING_NEWS_COUNT);
      if (latest.length === 0) { newsSection.remove(); return; }
      // The grid takes its column count from here rather than assuming three:
      // a deployment with one or two published articles should look like a
      // board with one or two notices, not like a three-up row that failed to
      // fill. See `.lp-news-home-list`.
      list.style.setProperty('--lp-news-n', String(latest.length));
      list.innerHTML = latest.map(newsCardMarkup).join('');
    });

    // Delegated, because the cards do not exist until the fetch lands. The
    // anchors are real hrefs, so a middle-click or a crawler still works;
    // this only keeps a plain click inside the SPA.
    //
    // `stopPropagation` for the reason `landing-chrome.ts` spells out at
    // `onPlay`: `main.ts` has a delegated handler on `document` that
    // navigates for ANY same-origin link, and without this both run and the
    // same path is pushed onto the history stack twice — so Back takes two
    // presses to leave an article.
    const onNewsClick = (e: Event): void => {
      const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[data-news-slug]');
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      opts.navigate(a.getAttribute('href')!);
    };
    list.addEventListener('click', onNewsClick);
    cleanups.push(() => list.removeEventListener('click', onNewsClick));
  }

  return () => {
    for (const fn of cleanups) fn();
    root.innerHTML = '';
  };
}

// ─── markup ──────────────────────────────────────────────────────────────────

function markup(): string {
  const { playHref, playLabel } = playCta();
  const signedIn = playHref === '/play';

  return `
<div class="lp">
  <div class="lp-grain"></div>

  ${headerMarkup(true)}

  <main>
    <section class="lp-hero">
      <!-- The poster image and its fallback gradient are both layers of one
           background-image on .lp-hero-poster, over in landing-styles.ts.
           Setting the URL inline here instead replaced the whole layer stack,
           so a missing poster file left a flat black hero rather than the
           gradient it was supposed to fall back to. -->
      <div class="lp-hero-scene" aria-hidden="true">
        <div class="lp-hero-poster"></div>
      </div>
      <div class="lp-hero-veil" aria-hidden="true"></div>
      <div class="lp-hero-copy">
        <p class="lp-motto">${escapeHtml(BRAND.motto)}</p>
        <h1 class="lp-title">
          <img src="/landing/logo-hero.webp"
            alt="${escapeAttr(BRAND.name)} — ${escapeAttr(BRAND.settlement)}" width="1400" height="609">
        </h1>
        <p class="lp-pitch">${escapeHtml(BRAND.pitch)}</p>
        <div class="lp-hero-actions">
          <a class="lp-btn lp-btn-primary lp-btn-lg" href="${escapeAttr(playHref)}" data-play>
            ${ICON_PLAY}<span>${escapeHtml(playLabel)}</span>
          </a>
          <a class="lp-btn lp-btn-lg" href="#${SECTIONS[0]!.id}">
            <span>See what it is</span>${ICON_CHEVRON_DOWN}
          </a>
        </div>
      </div>
    </section>

    <!-- Everything below the hero is wrapped in ONE element so it can carry
         ONE scrim. Per-section scrims were tiled edge to edge and a 22 px
         seam still opened under the hero, which on a live backdrop is a strip
         of raw settlement running across the page. A single box cannot have a
         seam in it. -->
    <div class="lp-blocks">
    ${SECTIONS.map(section).join('')}

    <!-- What's new.
         NOTE: no backticks in this comment. Everything below is one template
         literal, and a stray one closes it — esbuild then blames a line
         nowhere near the real cause.

         Inside .lp-blocks with everything else, because that wrapper carries
         the ONE scrim — a section outside it opens a seam of raw settlement
         across the page on a live backdrop.

         .lp-reveal is on the SECTION, never on the cards. renderLanding
         snapshots .lp-reveal synchronously at mount and hands that array to
         the IntersectionObserver, so anything injected later is never
         observed and would sit at its pre-reveal state for good — silently,
         with every test still green. -->
    <section class="lp-section lp-news-home lp-reveal" id="whats-new" data-news-section>
      <div class="lp-news-home-head">
        <div>
          <h2 class="lp-h2">What's new</h2>
          <p class="lp-body">
            The latest off the board at ${escapeHtml(BRAND.settlement)} — patch notes,
            what the scouts brought back, and the occasional notice nailed up by the gate.
          </p>
        </div>
        <a class="lp-btn" href="/news"><span>All dispatches</span></a>
      </div>
      <div class="lp-news-home-list" data-news-list></div>
    </section>

    <section class="lp-cta lp-reveal">
      <h2>The fire is still lit</h2>
      <p>
        ${escapeHtml(BRAND.settlement)} has a spare bunk, a workbench nobody is using,
        and a city full of things worth the walk. No download, no client — it runs in this tab.
      </p>
      <div class="lp-hero-actions">
        <a class="lp-btn lp-btn-primary lp-btn-lg" href="${escapeAttr(playHref)}" data-play>
          ${ICON_PLAY}<span>${escapeHtml(playLabel)}</span>
        </a>
        ${signedIn ? '' : '<a class="lp-btn lp-btn-lg" href="/login"><span>I already have an account</span></a>'}
      </div>
    </section>
    </div>
  </main>

  ${footerMarkup(true)}
</div>`;
}

/**
 * One content block.
 *
 * No eyebrow — Janeks: *"remove .lp-eyebrow items"*. The class still exists
 * for the news pages, where it carries the dispatch tag and date and is doing
 * a different job.
 *
 * The preview slot is EMPTY here and filled later by a dynamic import, if at
 * all: see `mountPreviews` in this file. A section with no preview has no slot
 * element, so the copy uses the full width.
 */
/**
 * One content block.
 *
 * No eyebrow — Janeks: *"remove .lp-eyebrow items"*. The class still exists
 * for the news pages, where it carries the dispatch tag and date and is doing
 * a different job.
 *
 * Two shapes, decided by whether the section has a preview:
 *
 *   none    copy full width.
 *   survivor/weapon
 *           copy in a column with the turntable beside it, both using the
 *           same `.lp-section-split` two-column layout — Janeks: *"this
 *           needs to be in 2 columns. 1 column text and data, other - 3D
 *           weapon model. Same as the character block."* The two kinds only
 *           differ in which model they load and how it's framed
 *           (`landing-preview.ts`), not in page layout.
 *
 * The preview slot is EMPTY in the markup and filled later by a dynamic
 * import, if at all — see `mountPreviews` in this file.
 */
function section(s: (typeof SECTIONS)[number]): string {
  const stats = `
      <div class="lp-stats lp-stats-${s.statStyle}">
        ${s.stats.map((st) => `
          <div class="lp-stat">
            <span class="lp-stat-value">${escapeHtml(st.value)}</span>
            <span class="lp-stat-label">${escapeHtml(st.label)}</span>
          </div>`).join('')}
      </div>`;

  const points = `
        <ul class="lp-points">
          ${s.points.map((p) => `<li>${escapeHtml(p)}</li>`).join('')}
        </ul>`;

  const heading = `
        <h2 class="lp-h2">${escapeHtml(s.heading)}</h2>
        <p class="lp-body">${escapeHtml(s.body)}</p>`;

  if (!s.preview) {
    return `
    <section class="lp-section lp-reveal" id="${s.id}">${heading}${stats}${points}
    </section>`;
  }

  return `
    <section class="lp-section lp-reveal lp-has-preview lp-has-preview-${s.preview}" id="${s.id}">
      <div class="lp-section-split">
        <div class="lp-section-copy">${heading}${points}${stats}
        </div>
        <div class="lp-preview" data-preview="${s.preview}" aria-hidden="true"></div>
      </div>
    </section>`;
}
